import React, { useState, useEffect, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { loadBlogContent, getBlogData, loadPostContent, loadVisualization } from "../utils/blogLoader";
import PasswordProtection from "./PasswordProtection";
import Hero from "./Hero";
import { useBlogStore } from '../stores/blogStore';
import { useProgressStore } from '../stores/progressStore';
import { useNavigationStore } from '../stores/navigationStore';
import type { BlogPost } from '../types';
import BlogCard from './BlogCard';
import PostContent from './PostContent';

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const FilterButton: React.FC<FilterButtonProps> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`
      px-4 py-2 rounded-full text-sm font-medium transition-all
      ${active
        ? 'bg-blue-500 text-white'
        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
      }
    `}
  >
    {children}
  </button>
);

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useNavigationStore();
  
  const {
    posts,
    selectedPost,
    filters,
    loading,
    error,
    setPosts,
    setSelectedPost,
    closeModal,
    setFilters,
    setLoading,
    setError,
  } = useBlogStore();

  const { isUnlocked, updateProgress } = useProgressStore();
  const [visualizationComponents, setVisualizationComponents] = useState<Record<string, React.ComponentType>>({});

  useEffect(() => {
    const loadBlogPosts = async () => {
      setLoading(true);
      try {
        const blogPosts = getBlogData();
        
        if (!blogPosts?.length) {
          setError('No posts found');
          return;
        }

        const postsWithContent = await Promise.all(
          blogPosts.map(async (post) => {
            const content = await loadBlogContent(post.contentPath);
            return {
              ...post,
              content: content?.content || null
            };
          })
        );

        setPosts(postsWithContent);
      } catch (error) {
        setError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    loadBlogPosts();
  }, [setLoading, setError, setPosts]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const postId = params.get('post');
    
    if (postId) {
      const post = posts.find(p => p.id === postId);
      if (post) {
        setSelectedPost(post);
      }
    }
  }, [location.search, posts, setSelectedPost]);

  // Load visualizations when a post is selected
  useEffect(() => {
    if (!selectedPost?.hasVisualization) return;

    const loadVisualizations = async () => {
      try {
        const componentsToLoad = Array.isArray(selectedPost.visualization) 
          ? selectedPost.visualization 
          : selectedPost.visualization ? [selectedPost.visualization] : [];
          
        console.log("Components to load:", componentsToLoad);
        
        const loadedComponents: Record<string, React.ComponentType> = {};
        
        await Promise.all(componentsToLoad.map(async (vizPath) => {
          try {
            // Extract component name from path
            const componentName = vizPath.includes('/') 
              ? vizPath.split('/').pop()?.replace(/\.(jsx|tsx)$/, '') || '' 
              : vizPath.replace(/\.(jsx|tsx)$/, '');
            
            // Load the visualization
            const component = await loadVisualization(selectedPost.id, vizPath);
            
            if (component) {
              // Store both with and without extension for better matches
              const cleanName = componentName.replace(/\.(jsx|tsx)$/, '');
              loadedComponents[cleanName] = component;
              
              // Also store with the full name if it includes an extension
              if (vizPath.endsWith('.jsx') || vizPath.endsWith('.tsx')) {
                loadedComponents[vizPath] = component;
              }
            }
          } catch (error) {
            console.error(`Failed to load visualization: ${vizPath}`, error);
          }
        }));
        
        setVisualizationComponents(loadedComponents);
      } catch (error) {
        console.error('Failed to load visualizations:', error);
      }
    };
    
    loadVisualizations();
    
    // Cleanup on post change
    return () => {
      setVisualizationComponents({});
    };
  }, [selectedPost]);

  const handlePostSelect = (post: BlogPost) => {
    setSelectedPost(post);
    navigate(`/blog?post=${post.id}`);
  };

  const handleBackToBlog = () => {
    closeModal();
    navigate('/blog', { replace: true });
  };

  const handleUnlock = (postId: string) => {
    updateProgress(postId, { isUnlocked: true });
  };

  // Find visualization component by name
  const findVisualizationComponent = (name: string) => {
    // Handle common name transformations
    const cleanName = name.replace(/\.(jsx|tsx)$/, '');
    
    // First, try exact match
    if (visualizationComponents[cleanName]) {
      return visualizationComponents[cleanName];
    }
    
    // Try with common suffix patterns
    const withViz = `${cleanName}Visualization`;
    const withChart = `${cleanName}Chart`;
    
    if (visualizationComponents[withViz]) {
      return visualizationComponents[withViz];
    }
    
    if (visualizationComponents[withChart]) {
      return visualizationComponents[withChart];
    }
    
    // Try case-insensitive match
    const lowerName = cleanName.toLowerCase();
    
    for (const [key, component] of Object.entries(visualizationComponents)) {
      if (key.toLowerCase() === lowerName) {
        return component;
      }
    }
    
    return null;
  };

  // Filter posts based on search and tags
  const filteredPosts = Array.isArray(posts) ? posts.filter(post => {
    if (!post) return false;
    
    const matchesSearch = !filters.search || 
      post.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      post.summary?.toLowerCase().includes(filters.search.toLowerCase());

    const matchesTags = !filters.tags || filters.tags.length === 0 ||
      filters.tags.every(tag => post.tags?.includes(tag));

    return matchesSearch && matchesTags;
  }) : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-black to-gray-900">
        <div className="text-white text-xl flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          Loading posts...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-black to-gray-900">
        <div className="text-red-500 text-xl flex items-center gap-3">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {error}
        </div>
      </div>
    );
  }

  // Render blog detail view
  if (selectedPost) {
    const normalizedPost = {
      ...selectedPost,
      visualization: typeof selectedPost.visualization === 'string' 
        ? selectedPost.visualization 
        : Array.isArray(selectedPost.visualization) 
          ? selectedPost.visualization[0] 
          : undefined
    };

    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 pb-24 md:pb-8">
        <div className="sticky top-0 z-10 bg-gray-900 bg-opacity-95 backdrop-blur-sm border-b border-gray-800 flex items-center p-4">
          <h1 className="text-xl font-semibold text-white truncate ml-10">{normalizedPost.title}</h1>
          <button
            onClick={handleBackToBlog}
            className="mr-3 text-gray-400 hover:text-white flex items-center ml-auto"
            aria-label="Back to blog"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="ml-1 text-sm hidden sm:inline">Back to Blog</span>
          </button>
        </div>
        
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
          {normalizedPost.isPasswordProtected && !isUnlocked(normalizedPost.id) ? (
            <div className="flex items-center justify-center min-h-[50vh]">
              <PasswordProtection 
                onUnlock={() => handleUnlock(normalizedPost.id)} 
                postId={normalizedPost.id}
              />
            </div>
          ) : (
            <div className="prose prose-invert max-w-none">
              <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
                <span>{normalizedPost.metadata.author}</span>
                <span>•</span>
                <span>{normalizedPost.metadata.readTime} min read</span>
                <span>•</span>
                <span>{normalizedPost.date}</span>
              </div>
              <PostContent 
                contentPath={normalizedPost.contentPath} 
                onVisualizationMarker={(vizName) => {
                  console.log("Blog received visualization marker:", vizName);
                  const MatchingComponent = findVisualizationComponent(vizName);
                  
                  if (MatchingComponent) {
                    return (
                      <div className="my-8">
                        <Suspense fallback={<div className="text-white">Loading visualization...</div>}>
                          <MatchingComponent />
                        </Suspense>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="my-8 p-4 bg-red-900 bg-opacity-50 rounded">
                      <p className="text-white">Visualization not found: {vizName}</p>
                      <p className="text-gray-400 text-sm">
                        Available components: {Object.keys(visualizationComponents).join(', ')}
                      </p>
                    </div>
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render blog list view
  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      <Hero 
        data={{
          title: "Blog",
          description: "Thoughts, insights, and experiences shared through writing",
          background: 'piano1'
        }}
      />
      
      <div className="py-2 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-black/30 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 border border-gray-800"
          >
            <div className="flex flex-wrap gap-2 md:gap-4 mb-6">
              <FilterButton
                active={!filters.tags || filters.tags.length === 0}
                onClick={() => setFilters({ ...filters, tags: [] })}
              >
                All
              </FilterButton>
              {Array.from(new Set(posts?.flatMap(post => post.tags || []) || [])).map(tag => (
                <FilterButton
                  key={tag}
                  active={filters.tags?.includes(tag)}
                  onClick={() => {
                    const newTags = filters.tags?.includes(tag)
                      ? filters.tags.filter(t => t !== tag)
                      : [...(filters.tags || []), tag];
                    setFilters({ ...filters, tags: newTags });
                  }}
                >
                  {tag}
                </FilterButton>
              ))}
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search posts..."
                value={filters.search || ''}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full md:w-64 px-4 py-2 pl-10 rounded-lg bg-black/50 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-800"
              />
              <svg 
                className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </motion.div>

          <motion.div 
            layout
            className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          >
            <AnimatePresence>
              {filteredPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <BlogCard
                    post={post}
                    onClick={handlePostSelect}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredPosts.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-gray-400 text-lg">No posts match your filters</p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;
